import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import { RotateCcw, Lightbulb } from 'lucide-react';
import { useTranslation } from '../../../i18n';

const BASE_TILE_SIZE = 44;
const BASE_GAP = 8;
const BASE_PADDING_X = 16;
const BASE_PADDING_Y = 14;
const BASE_BUTTON_FONT = 14;

const normalizeIndices = (word, indices) => {
  const len = (word || '').length;
  if (!Array.isArray(indices)) return [];
  return indices
    .map((n) => Number(n))
    .filter((n) => Number.isInteger(n) && n >= 0 && n < len);
};

// Compares user's per-index letters against the correct word (case-insensitive).
// Returns { isCorrect, perLetterResults } where perLetterResults maps missing
// index → boolean.
export const gradeMissingLetters = (word, missingIndices, answer) => {
  const normIndices = normalizeIndices(word, missingIndices);
  const results = {};
  let allCorrect = normIndices.length > 0;
  normIndices.forEach((idx) => {
    const expected = (word[idx] || '').toLowerCase();
    const got = ((answer || {})[idx] || '').toString().toLowerCase();
    const ok = !!expected && got === expected;
    results[idx] = ok;
    if (!ok) allCorrect = false;
  });
  return { isCorrect: allCorrect, perLetterResults: results };
};

const MissingLettersElement = ({
  element,
  isEditor = false,
  studentView = false,
  currentAnswer = null,
  showResult = false,
  isCorrect = null,
  perLetterResults = null,
  onAnswerChange,
  onCheck,
  onRetry,
  disabled = false,
  scale = 1,
  hideCheckButton = false,
}) => {
  const { t } = useTranslation();
  const { content, style, id } = element;
  const {
    word = '',
    missingIndices = [],
    hint = '',
    allowRetry = false,
    fontFamily = 'Inter',
    fontSize = 22,
    textColor = '#2A4D25',
    tileColor = '#f9fafb',
    tileBorderColor = '#d1d5db',
    inputBgColor = '#ffffff',
    inputBorderColor = '#2A4D25',
  } = content || {};

  const backgroundColor = style?.backgroundColor || 'transparent';

  const containerRef = useRef(null);
  const inputsRef = useRef({});
  const [boxScale, setBoxScale] = useState(scale);

  const normIndices = useMemo(
    () => normalizeIndices(word, missingIndices),
    [word, missingIndices]
  );

  useLayoutEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    const letterCount = (word || '').length || 1;
    const update = (width, height) => {
      if (width <= 0 || height <= 0) return;
      const naturalRowContent = letterCount * BASE_TILE_SIZE + (letterCount - 1) * BASE_GAP;
      const availableWidth = Math.max(0, width - BASE_PADDING_X * 2);
      const naturalHeight = BASE_TILE_SIZE + 40 + (hint ? 32 : 0);
      const next = Math.min(availableWidth / naturalRowContent, height / naturalHeight) * scale;
      setBoxScale(Math.max(0.3, next));
    };
    update(node.clientWidth, node.clientHeight);
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        update(entry.contentRect.width, entry.contentRect.height);
      }
    });
    ro.observe(node);
    return () => ro.disconnect();
  }, [scale, word, hint]);

  const tileSize = Math.max(22, Math.floor(BASE_TILE_SIZE * boxScale));
  const tileFont = Math.max(12, Math.round(fontSize * boxScale));
  const tileGap = Math.max(2, Math.floor(BASE_GAP * boxScale));
  const tileRadius = Math.max(4, Math.round(10 * boxScale));
  const buttonFontSize = Math.max(10, Math.round(BASE_BUTTON_FONT * boxScale));
  const paddingX = Math.max(8, Math.round(BASE_PADDING_X * boxScale));
  const paddingY = Math.max(6, Math.round(BASE_PADDING_Y * boxScale));

  // Local per-letter answer map. In the editor it's always empty (preview
  // shows the input fields blank so the teacher sees the exercise shape).
  const [localAnswer, setLocalAnswer] = useState(currentAnswer || {});

  useEffect(() => {
    const external = currentAnswer || {};
    if (disabled || isEditor || showResult) {
      setLocalAnswer(external);
      return;
    }
    setLocalAnswer((prev) => {
      const prevEmpty = !prev || Object.keys(prev).length === 0;
      const externalEmpty = !external || Object.keys(external).length === 0;
      if (prevEmpty && !externalEmpty) return external;
      if (externalEmpty && !showResult) return {};
      return prev;
    });
  }, [currentAnswer, showResult, disabled, isEditor]);

  const handleLetterChange = (index, value) => {
    if (disabled || showResult || isEditor) return;
    const letter = (value || '').slice(-1);
    const next = { ...localAnswer, [index]: letter };
    setLocalAnswer(next);
    onAnswerChange?.(next);

    // Auto-advance focus to the next missing input when the student types a letter.
    if (letter) {
      const orderPos = normIndices.indexOf(index);
      const nextIdx = normIndices[orderPos + 1];
      if (nextIdx !== undefined) {
        const nextEl = inputsRef.current[nextIdx];
        nextEl?.focus();
      }
    }
  };

  const handleKeyDown = (e, index) => {
    if (disabled || showResult || isEditor) return;
    if (e.key === 'Backspace' && !localAnswer[index]) {
      const orderPos = normIndices.indexOf(index);
      const prevIdx = normIndices[orderPos - 1];
      if (prevIdx !== undefined) {
        const prevEl = inputsRef.current[prevIdx];
        prevEl?.focus();
      }
    } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && allFilled) {
      e.preventDefault();
      handleCheck();
    }
  };

  const allFilled = normIndices.length > 0
    && normIndices.every((idx) => (localAnswer[idx] || '').trim().length > 0);

  const handleCheck = () => {
    if (!allFilled) return;
    onCheck?.(localAnswer);
  };

  const handleRetry = () => {
    setLocalAnswer({});
    onRetry?.();
  };

  const missingSet = useMemo(() => new Set(normIndices), [normIndices]);

  return (
    <div
      ref={containerRef}
      className="w-full h-full flex flex-col rounded-xl overflow-hidden"
      style={{ backgroundColor }}
    >
      {hint && (
        <div className="px-4 py-2 border-b border-gray-100 bg-gray-50">
          <p className="text-sm text-gray-600 flex items-center gap-2">
            <Lightbulb size={14} className="text-amber-500" />
            {hint}
          </p>
        </div>
      )}

      <div
        className="flex-1 min-h-0 flex flex-col items-center justify-center overflow-hidden"
        style={{ paddingLeft: paddingX, paddingRight: paddingX, paddingTop: paddingY, paddingBottom: paddingY }}
      >
        {word ? (
          <div className="flex flex-wrap justify-center items-center" style={{ gap: `${tileGap}px` }}>
            {word.split('').map((letter, idx) => {
              const isHidden = missingSet.has(idx);
              if (!isHidden) {
                return (
                  <div
                    key={`${id}-static-${idx}`}
                    className="flex items-center justify-center font-bold uppercase"
                    style={{
                      width: `${tileSize}px`,
                      height: `${tileSize}px`,
                      fontSize: `${tileFont}px`,
                      borderRadius: `${tileRadius}px`,
                      backgroundColor: tileColor,
                      border: `1px solid ${tileBorderColor}`,
                      color: textColor,
                      fontFamily,
                    }}
                  >
                    {letter}
                  </div>
                );
              }

              const userLetter = isEditor ? '' : (localAnswer[idx] || '');
              let borderColor = inputBorderColor;
              let bgColor = inputBgColor;
              if (showResult && !isEditor) {
                const letterOk = perLetterResults
                  ? !!perLetterResults[idx]
                  : userLetter.toLowerCase() === (letter || '').toLowerCase();
                borderColor = letterOk ? '#22c55e' : '#ef4444';
                bgColor = letterOk ? '#dcfce7' : '#fee2e2';
              }

              return (
                <input
                  key={`${id}-input-${idx}`}
                  ref={(el) => { inputsRef.current[idx] = el; }}
                  type="text"
                  maxLength={1}
                  value={userLetter}
                  onChange={(e) => handleLetterChange(idx, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(e, idx)}
                  disabled={isEditor || disabled || showResult}
                  className="text-center font-bold uppercase focus:outline-none focus:ring-2 focus:ring-brand-primary/40 transition"
                  style={{
                    width: `${tileSize}px`,
                    height: `${tileSize}px`,
                    fontSize: `${tileFont}px`,
                    borderRadius: `${tileRadius}px`,
                    backgroundColor: bgColor,
                    border: `2px solid ${borderColor}`,
                    color: textColor,
                    fontFamily,
                  }}
                />
              );
            })}
          </div>
        ) : (
          <p className="text-sm text-gray-400">{t('games.missingLetters.enterWordInSettings')}</p>
        )}
      </div>

      {!isEditor && (
        <div
          className="flex items-center justify-end gap-2"
          style={{
            backgroundColor: 'transparent',
            paddingLeft: paddingX,
            paddingRight: paddingX,
            paddingTop: Math.max(4, Math.round(8 * boxScale)),
            paddingBottom: Math.max(4, Math.round(8 * boxScale)),
          }}
        >
          {showResult && !isCorrect && allowRetry && (
            <button
              onClick={handleRetry}
              className="flex items-center gap-1.5 px-3 py-1.5 font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition"
              style={{ fontSize: buttonFontSize }}
            >
              <RotateCcw size={Math.max(12, Math.round(14 * boxScale))} />
              {t('games.common.tryAgain')}
            </button>
          )}

          {!showResult && !hideCheckButton && (
            <button
              onClick={handleCheck}
              disabled={!allFilled}
              className={`px-4 py-1.5 font-medium rounded-lg transition ${
                allFilled
                  ? 'bg-indigo-500 text-white hover:bg-indigo-600'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
              style={{ fontSize: buttonFontSize }}
            >
              {t('games.common.check')}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default MissingLettersElement;
